import { SERVER_URL } from "../../../constants";

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${sessionStorage.getItem("bearer")}`,
  };
}

async function handle(res, fallback) {
  if (res.status === 204) return [];
  if (res.ok) return res.json();
  let message = fallback;
  try {
    const text = await res.text();
    if (text) message = text;
  } catch {
    /* ignore */
  }
  const err = new Error(message);
  err.status = res.status;
  throw err;
}

// Adult-backed agents only (AgentDTO[] with name/email/phone from the adult).
// withAdult=true drops the legacy adult-less rows server-side.
export async function getAgents() {
  const res = await fetch(`${SERVER_URL}api/agents?withAdult=true`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load agents");
}

// An agent's linked stays with per-stay snapshot + charge totals + outstanding flag.
export async function getAgentStays(agentId) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/stays`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load the agent's stays");
}

// All adults — used to find an existing adult to attach an agent to.
export async function getAdults() {
  const res = await fetch(`${SERVER_URL}api/adults`, { headers: authHeaders() });
  return handle(res, "Failed to load adults");
}

// Create a new adult record { adultName, adultSurname, adultEmail, adultPhone }.
export async function createAdult(adult) {
  const res = await fetch(`${SERVER_URL}api/adults`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(adult),
  });
  return handle(res, "Failed to create the adult");
}

// Create an agent tied to an adult. Body { commission, commissionRate,
// englishSpeaking, notes } — commission is the enum name NONE|NET|GROSS.
export async function createAgent(adultId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${adultId}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to create the agent");
}

// Update an agent's defaults. Body same fields as createAgent.
export async function updateAgent(agentId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to update the agent");
}

// Basic student list for a year — to pick a student when adding a connection.
export async function getStudentsBasicYear(year) {
  const res = await fetch(`${SERVER_URL}api/studentsBasicYear/${year}`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load students");
}

// A student's stays { stayId, arrivalDate, departureDate, campus, … }.
export async function getStudentStays(studentId) {
  const res = await fetch(`${SERVER_URL}api/student/${studentId}/stays`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load the student's stays");
}

// Bookings belonging to one stay (booking↔stay resolved server-side). Each has
// productName, startDate, endDate, actualCharge, commissionable — for invoicing.
export async function getStayBookings(studentId, stayId) {
  const res = await fetch(
    `${SERVER_URL}api/student/${studentId}/stay/${stayId}/bookings`,
    { headers: authHeaders() }
  );
  return handle(res, "Failed to load the stay's bookings");
}

// Link an agent to a stay; PUT updates the snapshot; DELETE unlinks. Body for
// link/update: { commission, commissionRate } (defaults from the agent).
export async function linkAgentStay(agentId, stayId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/stays/${stayId}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to link the stay");
}

export async function updateAgentStay(agentId, stayId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/stays/${stayId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to update the connection");
}

export async function deleteAgentStay(agentId, stayId) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/stays/${stayId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handle(res, "Failed to remove the connection");
}

// A single student record { id, studentName, studentSurname, mtRef, … } — for the
// per-stay invoice header (names + MT ref), matching the student Invoice tab.
export async function getStudent(studentId) {
  const res = await fetch(`${SERVER_URL}api/students/${studentId}`, {
    headers: authHeaders(),
  });
  const data = await handle(res, "Failed to load the student");
  return Array.isArray(data) ? null : data; // handle() yields [] on 204
}

// A student's payments (same source the Invoice tab uses for its Payments &
// Adjustments section). Returns [] on 204.
export async function getStudentPayments(studentId) {
  const res = await fetch(`${SERVER_URL}api/payments/${studentId}`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load the student's payments");
}

// Booking groups (type BOOKING). Each carries studentIds[] + adultIds[]. Used by
// the "Add by group" bulk-link flow to pull a whole group's students at once.
export async function getBookingGroups() {
  const res = await fetch(`${SERVER_URL}api/campgroups/type/BOOKING`, {
    headers: authHeaders(),
  });
  return handle(res, "Failed to load booking groups");
}

// A group's booking meta { defaultCampus, defaultStartDate, defaultEndDate, … }.
// Returns null when the group has no meta (backend 204).
export async function getGroupBookingMeta(groupId) {
  const res = await fetch(`${SERVER_URL}api/campgroup/${groupId}/booking-meta`, {
    headers: authHeaders(),
  });
  const data = await handle(res, "Failed to load the group's booking meta");
  return Array.isArray(data) ? null : data; // handle() yields [] on 204
}

// Batch resolve students' stays. Body { ids }. Returns rows
// [{ studentId, stayId, campus, arrivalDate, departureDate }, …].
export async function getStudentsStaysBatch(ids) {
  if (!ids?.length) return [];
  const res = await fetch(`${SERVER_URL}api/students/stays/batch`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ ids: Array.from(new Set(ids)) }),
  });
  return handle(res, "Failed to load the students' stays");
}

// Batch resolve student records (for names). Body { ids } → student[].
export async function getStudentsByIds(ids) {
  if (!ids?.length) return [];
  const res = await fetch(`${SERVER_URL}api/students/batch`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({ ids: Array.from(new Set(ids)) }),
  });
  return handle(res, "Failed to load students");
}

// Commission payments WE make to the agent.
export async function getAgentPayments(agentId, { from, to } = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString();
  const res = await fetch(
    `${SERVER_URL}api/agent/${agentId}/payments${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
  return handle(res, "Failed to load the agent's payments");
}

// Body { paymentDate, paymentAmount, paymentAccount }.
export async function createAgentPayment(agentId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/payments`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to record the payment");
}

// Update a commission payment. Body same fields as create.
export async function updateAgentPayment(agentId, paymentId, body) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/payments/${paymentId}`, {
    method: "PUT",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  return handle(res, "Failed to update the payment");
}

// Delete a commission payment.
export async function deleteAgentPayment(agentId, paymentId) {
  const res = await fetch(`${SERVER_URL}api/agent/${agentId}/payments/${paymentId}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  return handle(res, "Failed to delete the payment");
}

// Date-ordered ledger { lines:[STUDENT|PAYMENT], totalCommissionPayable,
// totalPaid, balance }. Always 200.
export async function getCommissionStatement(agentId, { from, to } = {}) {
  const params = new URLSearchParams();
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  const qs = params.toString();
  const res = await fetch(
    `${SERVER_URL}api/agent/${agentId}/commission-statement${qs ? `?${qs}` : ""}`,
    { headers: authHeaders() }
  );
  return handle(res, "Failed to load the commission statement");
}
